import { useState } from "react";
import { useGetStaffActivityLogsQuery } from "@/redux/services/pos/staffLog.api";
import { usePagination } from "@/lib/hooks";
import LogEntryCard from "./LogEntryCard";
import { Activity, ChevronLeft, ChevronRight } from "lucide-react";

interface StaffActivityListProps {
  restaurantId: string;
}

const ACTION_OPTIONS = [
  { value: "", label: "Toutes" },
  { value: "create", label: "Creation" },
  { value: "update", label: "Modification" },
  { value: "remove", label: "Suppression" },
];

function StaffActivityList({ restaurantId }: StaffActivityListProps) {
  const [staffUsername, setStaffUsername] = useState("");
  const [selectedAction, setSelectedAction] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const { page, limit, onChange } = usePagination(30);

  const { data, isLoading, isFetching } = useGetStaffActivityLogsQuery({
    restaurantId,
    query: {
      ...(staffUsername ? { staffUsername } : {}),
      ...(selectedAction ? { action: selectedAction } : {}),
      ...(dateFrom ? { dateFrom } : {}),
      ...(dateTo ? { dateTo } : {}),
      page,
      limit,
    },
  });

  const loading = isLoading || isFetching;
  const totalPages = data ? Math.ceil(data.count / limit) : 0;

  return (
    <div className="mt-6 space-y-6">
      {/* Filters */}
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Staff phone input */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Membre du personnel
            </label>
            <input
              type="text"
              placeholder="Numéro de téléphone"
              value={staffUsername}
              onChange={(e) => {
                setStaffUsername(e.target.value);
                onChange(limit, 1);
              }}
              className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Action chip selector */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Action
            </label>
            <div className="flex flex-wrap gap-2">
              {ACTION_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedAction(option.value);
                    onChange(limit, 1);
                  }}
                  className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                    selectedAction === option.value
                      ? "bg-primary text-white"
                      : "bg-muted text-muted-foreground hover:bg-muted"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date from */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Date debut
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => {
                setDateFrom(e.target.value);
                onChange(limit, 1);
              }}
              className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>

          {/* Date to */}
          <div>
            <label className="block text-xs font-medium text-muted-foreground mb-1">
              Date fin
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => {
                setDateTo(e.target.value);
                onChange(limit, 1);
              }}
              className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-border bg-card p-4 animate-pulse"
            >
              <div className="flex items-center gap-3">
                <div className="h-3 w-20 bg-muted rounded" />
                <div className="h-3 w-16 bg-muted rounded" />
                <div className="h-5 w-24 bg-muted rounded-full" />
              </div>
              <div className="mt-2 h-3 w-48 bg-muted rounded" />
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {!loading && (!data || data.results.length === 0) && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-border bg-card py-16 px-4 text-center">
          <Activity className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-muted-foreground text-sm">
            Aucune activite enregistree
          </p>
        </div>
      )}

      {/* Log entries */}
      {!loading && data && data.results.length > 0 && (
        <div className="space-y-3">
          {data.results.map((log) => (
            <LogEntryCard key={log.id} log={log} />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!loading && data && totalPages > 1 && (
        <div className="flex items-center justify-between rounded-xl border border-border bg-card px-4 py-3">
          <span className="text-sm text-muted-foreground">
            Page {page} sur {totalPages} ({data.count} resultats)
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => onChange(limit, Math.max(1, page - 1))}
              disabled={page <= 1}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => onChange(limit, Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-border text-muted-foreground hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffActivityList;
